import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss'
import { PlayerPlay, SpeakersIcon, TrashIcon } from '../../../assets';

interface IProps {
  value: File | null;
  onChange: (value: File | null, duration: string) => void;
}

const cn = createCn('speakers', classes)

const formatTime = (seconds: number) => {
  const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const VoiceRecord = ({ value, onChange }: IProps) => {
  const { t } = useTranslation();

  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerId = useRef<NodeJS.Timeout | null>(null);

  const handleStartRecording = () => {
    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `recorded_${Date.now()}.webm`, { type: 'audio/webm' });

        const audio = document.createElement('audio');
        audio.src = URL.createObjectURL(blob);
        audio.onloadedmetadata = () => {
          setDuration(formatTime(Math.floor(audio.duration)));
          onChange(file, formatTime(Math.floor(audio.duration)));
        };
        audio.load();
      };

      mediaRecorder.start();
      setRecording(true);
    });
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    if (timerId.current) clearInterval(timerId.current);
  };

  const handleDeleteRecording = () => {
    onChange(null, '00:00:00');
    setDuration(null);
  };

  useEffect(() => {
    return () => {
      if (timerId.current) clearInterval(timerId.current);
    };
  }, []);

  const bars = useMemo(
    () => Array.from({ length: 25 }, () => Math.random() * 16 + 4),
    [] 
  )


  return (
    <div className={cn('voice-record')}>
      {recording ? (
        <div className={cn('recording', { 'stop': true })}>
          <button onClick={handleStopRecording}>
            {t('speakers.stop-recording')}
          </button>
        </div>
      ) : value?.name ? (
        <div className={cn('voice', { 'bordered': true })}>
          <div className={cn('voice-play')}>
            <PlayerPlay />
          </div>
          <div className={cn('voice-bars-wrapper')}>
            <div className={cn('voice-bars')}>
              {bars.map((height, i) => (
                <div
                  key={i}
                  className={cn('voice-bar')}
                  style={{
                    height: `${height}px`,
                  }}
                ></div>
              ))}
            </div>
            <div className={cn('voice-duration')}>
              {duration}
            </div>
          </div>

          <div className={cn('voice-delete')} onClick={handleDeleteRecording}>
            <TrashIcon />
          </div>

        </div>
      ) : (
        <div className={cn('recording')}>
          <button onClick={handleStartRecording} >
            <SpeakersIcon />
            {t('speakers.record-voice')}
          </button>
        </div>

      )}
    </div>
  );
}

export default VoiceRecord